/**
 * Access-key hashing used by every login in the app.
 *
 * Stored format (user_access.access_key):  <salt>:<hash>
 *   salt = 16 random bytes as 32 hex chars
 *   hash = PBKDF2-SHA256(key, salt as UTF-8, 100,000 iterations, 256 bits) as 64 hex chars
 * Only this value is stored; the plain key can never be read back from the database.
 */

export const generateSalt = (): string => {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array).map(b => b.toString(16).padStart(2, '0')).join('');
};

export const hashAccessKey = async (accessKey: string, salt: string = generateSalt()): Promise<string> => {
  const encoder = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey('raw', encoder.encode(accessKey), 'PBKDF2', false, ['deriveBits']);
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', salt: encoder.encode(salt), iterations: 100000, hash: 'SHA-256' },
    keyMaterial,
    256
  );
  return salt + ':' + Array.from(new Uint8Array(bits)).map(b => b.toString(16).padStart(2, '0')).join('');
};

export const verifyAccessKey = async (accessKey: string, stored: string): Promise<boolean> => {
  const [salt] = stored.split(':');
  if (!salt) return false;
  return (await hashAccessKey(accessKey, salt)) === stored;
};

// Readable key such as "K7QF-93MX-TW4H-PZ8R" (no 0/O/1/I/L to avoid mix-ups), ~79 bits of randomness.
const KEY_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';

export const generateAccessKey = (groups = 4, groupSize = 4): string => {
  const limit = 256 - (256 % KEY_ALPHABET.length);
  const chars: string[] = [];
  while (chars.length < groups * groupSize) {
    const bytes = new Uint8Array(32);
    crypto.getRandomValues(bytes);
    for (const b of Array.from(bytes)) {
      if (b < limit && chars.length < groups * groupSize) chars.push(KEY_ALPHABET[b % KEY_ALPHABET.length]);
    }
  }
  const parts: string[] = [];
  for (let i = 0; i < groups; i++) parts.push(chars.slice(i * groupSize, (i + 1) * groupSize).join(''));
  return parts.join('-');
};
